/*
  HM Atlas Frontend
  Table Field utility functions.
*/

/* ---------------------------------------------------------- */
/* Generic object properties. */

/**
 * Set readonly Named Value property against object: obj.name=value;
 * - Enumerable=true    to ensure that property is serialisable.
 * - Writeable=false    to ensure that property is readonly.
 * - Configurable=false to ensure that property cannot be deleted or have its attributes changed.
 * @param {Object} obj object to set property against.
 * @param {string} name property name.
 * @param {any} value property value.
 */
export const setReadOnlyProp = (obj, name, value) => {
  Object.defineProperty(obj, name, { value: value, enumerable: true, writable: false, configurable: false });
};

/** Assign readonly Named Value properties against object: obj.name=value; */
export const assignReadOnlyProps = (obj, props) => {
  for (const [key, value] of Object.entries(props))
    setReadOnlyProp(obj, key, value);
};

/** Set readonly Value property against object: obj.value=value; */
const setValueProp = (obj, value) => {
  setReadOnlyProp(obj, [value], value);
};
/** Set readonly Value properties against object: obj.value=value; */
const setValueProps = (obj, values) => {
  for (const value of values)
    setValueProp(obj, value);
};

/** Set readonly value/text properties against object: obj.value=value; obj.text=text; */
const setValueTextProps = (obj, value, text) => {
  setReadOnlyProp(obj, 'value', value);
  setReadOnlyProp(obj, 'text', text);
};

/** Create Object with value and text properties. */
const getValueText = (value, text) => {
  const option = {};
  setValueTextProps(option, value, text);
  return option;
};

/* ---------------------------------------------------------- */
/* Table properties. */

/** Template Table schema. */
const SCHEMA_INITIAL_DATA = {};
assignReadOnlyProps(SCHEMA_INITIAL_DATA, {
  name: '',
  description: '',
  fields: [],
  toggleColumns: [],
  folder: {
    module: {},
  },
  displayName: '',
  currentUserPermissionLevel: null,
});
Object.freeze(SCHEMA_INITIAL_DATA);
export { SCHEMA_INITIAL_DATA };

/* ---------------------------------------------------------- */
/* Field properties. */

/**
 * Named data type values.
 * - eg. To test: fieldType === dataTypes.boolean.
 */
const dataTypes = {};
setValueProps(dataTypes, [
  'text', 'wholeNumber', 'decimalNumber', 'floatNumber',
  'boolean', 'date', 'dateTime', 'lookup', 'parentChildHierarchy', 'document']);
Object.freeze(dataTypes);
export { dataTypes };

/** Template Field definition. */
const FIELD_INITIAL_DATA = {};
assignReadOnlyProps(FIELD_INITIAL_DATA, {
  type: dataTypes.text,
  name: '',
  label: '',
  displayInTable: true,
  displayInForm: true,
  isRequired: false,
  dataTypeName: 'text',
  dataTypeJSON: {},
  description: '',
});
Object.freeze(FIELD_INITIAL_DATA);
export { FIELD_INITIAL_DATA };

/**
 * Extract the named property from the Field's dataTypeJSON value.
 * - NB dataTypeJSON is a JSON string or object.
 * - Property examples: MaximumCharacters, IsCaseSensitive, Precision, FieldToDisplayGuid.
 * @param {Object} field definition object.
 * @param {String} key property name.
 * @returns Named property.
 */
export const getFieldDataTypeProperty = (field, key) => {
  let dataTypeJSON = field.dataTypeJSON;
  if (!dataTypeJSON) return null;
  if (typeof dataTypeJSON === 'string') dataTypeJSON = JSON.parse(dataTypeJSON);
  return dataTypeJSON[key];
};

/* ---------------------------------------------------------- */
/* Field Data Type. */

/**
 * List of Data Types with captions suitable for dropdown.
 * - eg. { value: 'boolean', text: 'Checkbox' }
 */
const dataTypeOptions = [
  getValueText(dataTypes.text, 'Text'),
  getValueText(dataTypes.wholeNumber, 'Whole Number'),
  getValueText(dataTypes.decimalNumber, 'Decimal'),
  getValueText(dataTypes.floatNumber, 'Float'),
  getValueText(dataTypes.boolean, 'Checkbox'),
  getValueText(dataTypes.date, 'Date'),
  getValueText(dataTypes.dateTime, 'DateTime'),
  getValueText(dataTypes.lookup, 'Lookup'),
  getValueText(dataTypes.parentChildHierarchy, 'Parent Child Hierarchy'),
  getValueText(dataTypes.document, 'Document'),
];
Object.freeze(dataTypeOptions);
export { dataTypeOptions };

/**
 * Return the field's data type.
 * - fieldType if defined, otherwise dataTypeName.
 * @param {Object} field definition object
 * @returns {string} Field's data type (string).
 */
export const getFieldDataType = (field) => {
  return field?.fieldType || field?.dataTypeName;
};

/**
 * Return the field's data type name.
 * @param {Object} field definition object
 * @returns {string} Field's data type name.
 */
export const getFieldDataTypeName = (field) => {
  const dataType = getFieldDataType(field);
  return dataTypeOptions.find(dt => dt.value == dataType)?.text ?? dataType;
};

/**
 * Does the data type correspond to type 'text'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'text'.
 */
export const dataTypeIsText = (dataType) => {
  return (dataType === dataTypes.text);
};

/**
 * Determine if the Field is of type 'Text'.
 * @param {*} field definition.
 * @returns True if Field's Data Type is 'Text'.
 */
export const fieldIsText = (field) => {
  return (getFieldDataType(field) === dataTypes.text);
};

/**
 * Does the data type correspond to type 'Boolean'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'Boolean'.
 */
export const dataTypeIsBoolean = (dataType) => {
  return (dataType === dataTypes.boolean);
};

/**
 * Does the data type correspond to type 'Integer'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'Integer'.
 */
export const dataTypeIsInteger = (dataType) => {
  return (dataType === dataTypes.wholeNumber);
};

/**
 * Does the data type correspond to type 'Decimal'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'Decimal'.
 */
export const dataTypeIsDecimal = (dataType) => {
  return (dataType === dataTypes.decimalNumber);
};

/**
 * Does the data type correspond to type 'Float'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'Float'.
 */
export const dataTypeIsFloat = (dataType) => {
  return (dataType === dataTypes.floatNumber);
};

/**
 * Determine whether the data type is a numeric format (integer, decimal, float)
 * @param {string} dataType name
 * @returns True if data type is integer, decimal or float.
 */
export const dataTypeIsAnyNumeric = (dataType) => {
  return dataTypeIsInteger(dataType) || dataTypeIsDecimal(dataType) || dataTypeIsFloat(dataType);
};

/**
 * Does the data type correspond to type 'DateTime'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'DateTime'.
 */
export const dataTypeIsDateTime = (dataType) => {
  return (dataType === dataTypes.dateTime);
};

/**
 * Does the data type correspond to type 'Date'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'Date'.
 */
export const dataTypeIsDate = (dataType) => {
  return (dataType === dataTypes.date);
};

/**
 * Does the data type correspond to any 'Date/Time' format.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'Date' or 'DateTime'.
 */
export const dataTypeIsAnyDateTime = (dataType) => {
  return (dataType === dataTypes.date || dataType === dataTypes.dateTime);
};

/**
 * Does the data type correspond to type 'Lookup'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'Lookup'.
 */
export const dataTypeIsLookup = (dataType) => {
  return (dataType === dataTypes.lookup);
};

/**
 * Does the data type correspond to type 'ParentChild'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'ParentChild'.
 */
export const dataTypeIsParentChild = (dataType) => {
  return (dataType === dataTypes.parentChildHierarchy);
};

/**
 * Does the data type correspond to type 'Document'.
 * @param {string} dataType 
 * @returns True if data type corresponds to type 'Document'.
 */
export const dataTypeIsDocument = (dataType) => {
  return (dataType === dataTypes.document);
};

/* ---------------------------------------------------------- */
/* Change Field Data Type logic. */

/**
 * Determine whether the user should be asked for confirmation before changing the Field's data type.
 * - eg. Field currently has a calculation that is incompatible with the new data type.
 * @param {Object} field definition object.
 * @param {String} newDataType value selected by user.
 * @returns Message text if user if to provide confirmation of the data type change.
 */
export const getDataTypeChangeMessage = (field, newDataType) => {
  const dataType = getFieldDataType(field);
  if (!dataType || !newDataType || dataType === newDataType) return null;
  if (dataTypeIsText(dataType)) {
    if (fieldHasCalculation(field))
      return 'Changing from Text to another type will lose the current field\'s calculation.';
    return;
  }
  if (dataTypeIsLookup(dataType)) {
    if (fieldHasCalculation(field))
      return 'Changing from Lookup to another type will lose the current field\'s calculation.';
    return;
  }
  return null;
};

/**
 * Reset the Field's properties in line with its Data Type.
 * - Original logic from both TableBuilder and FieldModal (Data Type change event handlers).
 * @param {Object} field definition object.
 */
export const resetDataTypeProperties = (field) => {
  const dataType = getFieldDataType(field);
  const dataTypeJSONPrevious = field.dataTypeJSON;
  /* Initialise Data Type data. */
  field.dataTypeJSON = JSON.parse(JSON.stringify(FIELD_INITIAL_DATA.dataTypeJSON));
  field.columnAlignment = null;
  field.calculation = null;
  /* Default properties by Data Type. */
  if (dataType === 'select') {
    // 'Select' field.
    field.dataTypeJSON = { ...field.data, options: [] };
  } else if (dataTypeIsText(dataType)) {
    /* 'Text' field. */
    field.dataTypeJSON.MinimumCharacters = 0;
    field.dataTypeJSON.MaximumCharacters = -1;
  } else if (dataTypeIsInteger(dataType)) {
    /* 'Integer' field. */
    field.dataTypeJSON.FormatString = 'comma';
    field.columnAlignment = 'right';
  } else if (dataTypeIsDecimal(dataType) || dataTypeIsFloat(dataType)) {
    /* 'Decimal' or 'Float' field. */
    field.dataTypeJSON.Precision = dataTypeJSONPrevious.Precision || 20;
    field.dataTypeJSON.Scale = dataTypeJSONPrevious.Scale || 6;
    field.dataTypeJSON.DecimalsToDisplay = dataTypeJSONPrevious.DecimalsToDisplay || 2;
    field.dataTypeJSON.FormatString = 'comma';
    field.columnAlignment = 'right';
  } else if (dataTypeIsParentChild(dataType)) {
    /* 'ParentChild' field. */
    field.dataTypeJSON.ControlType = 'select';
  } else if (dataTypeIsAnyDateTime(dataType)) {
    /* 'DateTime' or 'Date' field. */
    field.dataTypeJSON.FormatString = 'YYYY/MM/DD';
  } else if (dataTypeIsLookup(dataType)) {
    /* 'Lookup' field. */
    field.dataTypeJSON.SourceRelatedFieldPair = [{ SourceField: '', RelatedField: '' }];
  } else if (dataTypeIsDocument(dataType)) {
    /* 'Document' field. */
    field.dataTypeJSON.SPDefaultSiteId = '';
    field.dataTypeJSON.SPDefaultDriveId = '';
    field.dataTypeJSON.SPDefaultFolderId = '';
    field.dataTypeJSON.SPDefaultFolderPath = '';
    field.dataTypeJSON.MaxFilesPerRow = 999;
  }
}

/* ---------------------------------------------------------- */
/* Field Options. */

/**
 * Clear Field Options where not allowed.
 * @param {*} field definition.
 */
export const clearFieldOptionsNotAllowed = (field) => {
  if (!fieldOptionAllowed_Required(field)) field.isRequired = false;
  if (!fieldOptionAllowed_PredictiveSearch(field)) field.dataTypeJSON.UsePredictiveSearch = false;
}

/**
 * Determine whether the Field is allowed to have the 'Required' option.
 * @param {*} field definition.
 * @returns True if Field is allowed 'Required' option.
 */
export const fieldOptionAllowed_Required = (field) => {
  /* AI Chat field cannot be 'Required'. */
  if (fieldCalculationIsAIChat(field)) return false;
  return true;
};

/**
 * Determine whether the Field is allowed to have the 'Predictive Search' option.
 * @param {*} field definition.
 * @returns True if Field is allowed 'Predictive Search' option.
 */
export const fieldOptionAllowed_PredictiveSearch = (field) => {
  /* Only Text Fields can be 'Predictive Search'. */
  if (!fieldIsText(field)) return false;
  /* AI Chat field cannot be 'Predictive Search'. */
  if (fieldCalculationIsAIChat(field)) return false;
  return true;
};

/* ---------------------------------------------------------- */
/* Field Calculation. */

/**
 * Named Calculation Type values.
 * - eg. calculationTypes.lookup === 'lookup'
 */
const calculationTypes = {};
setValueProps(calculationTypes, [
  'concatenation', 'AIChat', 'RowFormula', 'Aggregation', 'MdmAggregation', 'lookup',
  /* Awaiting implementation. */
  // 'allocation'
]);
Object.freeze(calculationTypes);
export { calculationTypes };

/**
 * List of Data Types with captions suitable for dropdown.
 * - eg. { value: 'boolean', text: 'Checkbox' }
 */
const calculationTypeOptions = [
  getValueText(calculationTypes.concatenation, 'Text Concatenation'),
  getValueText(calculationTypes.AIChat, 'AI Chat'),
  getValueText(calculationTypes.RowFormula, 'Row Formula'),
  getValueText(calculationTypes.Aggregation, 'Aggregation - Same Table'),
  getValueText(calculationTypes.MdmAggregation, 'Aggregation - Other Table'),
  getValueText(calculationTypes.lookup, 'Lookup'),
  //getValueText(calculationTypes.allocation, 'Allocation'),
];
Object.freeze(calculationTypeOptions);
export { calculationTypeOptions };

/**
 * Named Concatenation Type values.
 * - eg. calculationTypes.lookup === 'lookup'
 */
const concatenationTypes = {};
setValueProps(concatenationTypes, ['stringLiteral', 'mdmField', 'mdmFieldFromRelatedTable']);
Object.freeze(concatenationTypes);
export { concatenationTypes };

/**
 * List of Data Types with captions suitable for dropdown.
 * - eg. { value: 'boolean', text: 'Checkbox' }
 */
const concatenationTypeOptions = [
  getValueText(concatenationTypes.stringLiteral, 'String Literal'),
  getValueText(concatenationTypes.mdmField, 'MDM Field'),
  getValueText(concatenationTypes.mdmFieldFromRelatedTable, 'MDM Field From Related Table'),
];
Object.freeze(concatenationTypeOptions);
export { concatenationTypeOptions };

// Aggregate function.
const aggregateFunctions = [
  { name: 'Count', description: 'number of values', returnType: 'numeric', dataTypes: '' },
  { name: 'CountDistinct', display: 'Count(distinct)', description: 'number of unique values', returnType: 'numeric', dataTypes: '' },
  { name: 'Sum', description: 'accumulated value', returnType: 'numeric', dataTypes: 'numeric' },
  { name: 'Min', description: 'smallest value', returnType: 'numeric', dataTypes: 'numeric,datetimes' },
  { name: 'Max', description: 'largest value', returnType: 'numeric', dataTypes: 'numeric,datetimes' },
  { name: 'Average', display: 'Avg()', description: 'average value', returnType: 'numeric', dataTypes: 'numeric' },
  { name: 'Var', description: 'statistical variance', returnType: 'numeric', dataTypes: 'numeric' },
  { name: 'Varp', description: 'statistical variance for the population', returnType: 'numeric', dataTypes: 'numeric' },
];
Object.freeze(aggregateFunctions);
export { aggregateFunctions };

const calculationStatics = {};
setReadOnlyProp(calculationStatics, 'concatenationItemsKey', 'values');
setReadOnlyProp(calculationStatics, 'lookupPairsKey', 'sourceRelatedFieldPair');
setReadOnlyProp(calculationStatics, 'lookupPairsKeyJson', 'SourceRelatedFieldPair');
Object.freeze(calculationStatics);
export { calculationStatics };

/* ---------------------------------------------------------- */
// Field Calculation Type.

export const getFieldCalculationType = (field) => {
  return (field?.isCalculated ? field?.calculation?.fieldCalculationType : '') || '';
};

/**
 * Determine whether the Field has a 'Concatenation' calculation defined.
 * @param {*} field definition
 * @returns True if the Field has a 'Concatenation' calculation.
 */
export const fieldCalculationIsConcatenation = (field) => {
  return getFieldCalculationType(field) == calculationTypes.concatenation;
};

/**
 * Determine whether the Field has an 'AI Chat' calculation defined.
 * @param {*} field definition
 * @returns True if the Field has an 'AI Chat' calculation.
 */
export const fieldCalculationIsAIChat = (field) => {
  return getFieldCalculationType(field) == calculationTypes.AIChat;
};

/**
 * Determine whether the Field has a 'Row Formula' calculation defined.
 * @param {*} field definition
 * @returns True if the Field has a 'Row Formula' calculation.
 */
export const fieldCalculationIsRowFormula = (field) => {
  return getFieldCalculationType(field) == calculationTypes.RowFormula;
};

/**
 * Determine whether the Field has a 'Aggregation' calculation defined.
 * @param {*} field definition
 * @returns True if the Field has a ' Aggregation' calculation.
 */
export const fieldCalculationIsAggregation = (field) => {
  return getFieldCalculationType(field) == calculationTypes.Aggregation;
};

/**
 * Determine whether the Field has a 'MDM Aggregation' calculation defined.
 * @param {*} field definition
 * @returns True if the Field has a 'MDM Aggregation' calculation.
 */
export const fieldCalculationIsMdmAggregation = (field) => {
  return getFieldCalculationType(field) == calculationTypes.MdmAggregation;
};

/**
 * Determine whether the Field has a 'Lookup' calculation defined.
 * @param {*} field definition
 * @returns True if the Field has a 'Lookup' calculation.
 */
export const fieldCalculationIsLookup = (field) => {
  return getFieldCalculationType(field) == calculationTypes.lookup;
};

/* ---------------------------------------------------------- */
// Field Calculation logic.

/**
 * Is the Field allowed to have a Calculation defined.
 * @param {Object} field definition object
 * @param {Object} state to identify which Field Calculation types are active.
 * @returns True if field can have a calculation.
 */
export const isFieldCalculationAllowed = (field, state) => {
  return !fieldCalculationDeniedMessage(field, state);
};

/**
 * Get the textual explanation as to why the field canot have a calculation defined.
 * @param {Object} field definition object
 * @param {Object} state to identify which Field Calculation types are active.
 * @returns Message if field canot have a calculation, otherwise null.
 */
export const fieldCalculationDeniedMessage = (field, state) => {

  /* RLS Fields cannot have calculation. */
  if (field.isRLSField) return 'RLS field cannot have a calculation';

  const fieldType = getFieldDataType(field);
  const calcTypes = [];  // Calculation types associated with the Field Type.

  // Text fields - Concatenation & AI Chat.
  if (dataTypeIsText(fieldType))
    calcTypes.push(calculationTypes.concatenation, calculationTypes.AIChat);

  // Numeric and Date fields can have a variety of calculations, eg. 'Row Formula'.
  if (dataTypeIsAnyNumeric(fieldType) || dataTypeIsAnyDateTime(fieldType))
    calcTypes.push(calculationTypes.RowFormula, calculationTypes.Aggregation, calculationTypes.MdmAggregation);

  /* Lookup Fields */
  if (dataTypeIsLookup(fieldType)) {
    /* Calculation not allowed for Lookup fields where fuzzy matching is configured. */
    if (getFieldDataTypeProperty(field, 'FuzzyMatchConfig'))
      return 'Lookup field has fuzzy matching defined';
    calcTypes.push(calculationTypes.lookup);
  }

  // Check if any of the allowed types are active.
  if (calcTypes.find(type => fieldAllowsCalculationType(field, type, state))) return;
  return 'Field type does not support calculation';
};

/**
 * Does the field allow the specified calculation type.
 * @param {Object} field definition object.
 * @param {concatenationTypes|string} calcType as per calculationTypes.
 * @param {Object} state to identify which Field Calculation types are active.
 * @returns True if the field allows the calculation type.
 */
export const fieldAllowsCalculationType = (field, calcType, state) => {
  if (!state.getters['dataManagement/fieldCalculationTypeAllowed'](calcType)) return;
  const fieldType = getFieldDataType(field);
  switch (calcType) {
    case calculationTypes.concatenation:
      /* Concatenation - text fields only. */
      return dataTypeIsText(fieldType);
    case calculationTypes.RowFormula:
      /* Row Formula & Aggregations - any simple data type other than text. */
      return dataTypeIsBoolean(fieldType) || dataTypeIsAnyNumeric(fieldType) || dataTypeIsAnyDateTime(fieldType);
    case calculationTypes.Aggregation:
    case calculationTypes.MdmAggregation:
      /* Aggregations - any numeric or date data type. */
      return dataTypeIsAnyNumeric(fieldType) || dataTypeIsAnyDateTime(fieldType);
    case calculationTypes.lookup:
      /* Lookup - Lookup fields only. */
      return dataTypeIsLookup(fieldType);
    case calculationTypes.AIChat:
      /* AI Chat - Text fields only. */
      return dataTypeIsText(fieldType);
  }
};

/**
 * Does the table field have a calculation defined.
 * @param {Object} field definition object
 * @returns True if the field has a caulation defined.
 */
export const fieldHasCalculation = (field) => {
  const dataType = getFieldDataType(field);
  if (getFieldCalculationType(field).length) return true;
  if (dataTypeIsLookup(dataType)) {
    /*
      Apr25 AH Method of storing Calculation properties differs for Lookup Fields
      but may be brought into line at some stage.
      Ref. CalculationModal.storeCalculationData().
    */
    if (getFieldDataTypeProperty(field, 'IsCalculated') && (getFieldDataTypeProperty(field, 'SourceRelatedFieldPair') || []).length)
      return true;
  }
  return false;
};

/**
 * Construct the Calculation Data object used by Calculation Modal
 * - Used by CalculationModal to extract the Calculation Data from the Field.
 * - Used by the FieldItem to validate the Field's calculation.
 * @param {Object} field 
 * @returns calculationData object.
 */
export const extractCalculationDataFromField = (field) => {
  const dataType = getFieldDataType(field);
  let calculationData = null;
  if (dataTypeIsLookup(dataType)) {
    /*
      Apr25 AH Method of storing Calculation properties differs for Lookup Fields
      but may be brought into line at some stage.
      Ref. CalculationModal.storeCalculationData().
    */
    if (getFieldDataTypeProperty(field, 'IsCalculated')) {
      calculationData = {
        fieldCalculationType: calculationTypes.lookup,
        orderOfCalculation: getFieldDataTypeProperty(field, 'OrderOfCalculation'),
        sourceRelatedFieldPair: (getFieldDataTypeProperty(field, 'SourceRelatedFieldPair') || []).map((pair) => { return { sourceField: pair.SourceField, relatedField: pair.RelatedField }; }),
      };
    }
  }
  else {
    /* Standard Field Calculation properties format. */
    if (fieldHasCalculation(field))
      calculationData = field.calculation;
  }
  return calculationData;
};

/* ---------------------------------------------------------- */
// Field Calculation property rules.

/**
 * Can the specified Field be used as an 'MDM Field' in a Concatenation Calculation value.
 * - Field must not be RLS.
 * @param {*} field definition to be verified for 'MDM Field'.
 * @returns True if Field can be used in Concatenation Calculation as 'MDM Field'.
 */
export const fieldCanBeConcatenationMdmField = (field) => {
  if (field.isRLSField) return false;
  return true;
};

/**
 * Can the specified Field be used as an 'MDM Field From a Related Table' in a Concatenation Calculation value.
 * - Field must belng to the same Table.
 * - Field must not be RLS.
 * @param {*} field definition to be verified for 'Field to Display'.
 * @param {*} relatedField Target of Lookup Field in Related Table.
 * @returns True if Field can be used in Concatenation Calculation as 'Field to Display'.
 */
export const fieldCanBeConcatenationFieldToDisplay = (field, relatedField) => {
  if (field.entityGuid !== relatedField.entityGuid) return false;
  if (field.isRLSField) return false;
  return true;
};

export const fieldAllowsAggregateFunction = (field, functionName) => {
  const dataType = getFieldDataType(field);
  switch (functionName) {
    case 'Count':
    case 'CountDistinct':
      // Jul25 AH Excluded while BE logic is implemented.
      return false;
    case 'Sum':
    case 'Average':
      return dataTypeIsAnyNumeric(dataType);
    case 'Min':
    case 'Max':
      return dataTypeIsAnyNumeric(dataType) || dataTypeIsAnyDateTime(dataType);
  }
};

/* ---------------------------------------------------------- */
// Field Calculation Formula - Display Name / Guid conversion.

/**
 * Replace Field Names ({{name}}) with corresponding Field Guid
 * for formula to be submitted to the BE API.
 * @param {string} formula with Field Names.
 * @param {array} dataFields Field definitions to provide Name-Guid translation.
 * @param {array} sourceFields Alternative Field definitions to provide Name-Guid translation for 'ROW:' placeholders.
 * @returns Formula with each Field Name replaced with corresponding Guid.
 */
export const changeFieldNamesToGuids = (formula, dataFields, sourceFields) => {
  return formula.replaceAll(/{{+["'`]*(.*?)["'`]*}}+/g, (_, fld) => {
    let fields = dataFields;
    // Check for prefix.
    let prefix = '';
    if (fld.startsWith('ROW:')) {
      prefix = 'ROW:';
      fld = fld.slice(4);
      fields = sourceFields || dataFields;
    }
    /* Lookup the field by Name to get Guid. */
    let field = fields.find(x => x.displayName === fld);
    return `{{${prefix}${field?.fieldGuid ?? fld}}}`;
  });
};

/**
 * Replace Field Guids ({{guid}}) with corresponding Field Name
 * for formula to be presented to user.
 * @param {string} formula with Field Guids.
 * @param {array} dataFields Field definitions to provide Name-Guid translation.
 * @param {array} sourceFields Alternative Field definitions to provide Name-Guid translation for 'ROW:' placeholders.
 * @param {bool} includeQuotes Flag to enclose Field Name in single quotes to ensure valid javascript. Default: true.
 * @returns Formula with each Field Name replaced with corresponding Guid.
 */
export const changeFieldGuidsToNames = (formula, dataFields, sourceFields, includeQuotes = true) => {
  return formula.replaceAll(/{{+["'`]*(.*?)["'`]*}}+/g, (_, fld) => {
    let fields = dataFields;
    // Check for prefix.
    let prefix = '';
    if (fld.startsWith('ROW:')) {
      prefix = 'ROW:';
      fld = fld.slice(4);
      fields = sourceFields || dataFields;
    }
    /* Lookup the field by Guid to get Name. */
    const field = fields.find(x => x.fieldGuid === fld) || fields.find(x => x.displayName === fld);
    const name = field?.displayName ?? fld;
    return includeQuotes ? `{{'${prefix}${name}'}}` : `{{${prefix}${name}}}`;
  });
};

/* ---------------------------------------------------------- */
// Field Calculation Validation.

/**
 * Determine the (first/primary) Calculation Data validation failure.
 * - Supply field and fields parameters for deep validation (cross-reference fields etc). Otherwise only mandatory values checked.
 * - Used by the FieldItem to determine whether the Calculation Modal can be shown even if the field has unsaved changes.
 * - Used by the Calculation Modal to determine whether the changes can be saved and the Apply button tooltip.
 * @param {Object} calculationData calculation data object.
 * @param {Object} [currentField] current field definitions. [optional]
 * @param {Array} [allFields] list of all field definitions. [optional]
 * @returns {string} message to indicate Calculation Data validation failure.
 */
export const calculationDataValidationFailureMessage = (calculationData, currentField = null, allFields = null) => {

  const fieldCalculationType = calculationData?.fieldCalculationType ?? '';

  /* Check if Field is allowed to have a calculation. */
  if (currentField?.isRLSField && fieldCalculationType) return 'RLS field cannot have a calculation';

  /* Validate by Field Type. */
  switch (fieldCalculationType) {

    /* Empty Calculation. */
    case '':
      break;

    /* ----------------------------------------------------------- */
    /* Calculation Type - Text Field Concatenation. */
    case calculationTypes.concatenation:

      const values = calculationData.values;
      if (!values?.length) return 'At least one Concatenation clause must be supplied.';
      for (let idx = 0; idx < values.length; idx++) {
        /* Validate each value's properties by item type. */
        const value = values[idx];
        switch (value.itemType) {
          case concatenationTypes.stringLiteral:
            /* String LIteral - any populated text value allowed, including space(s). */
            if (!value.itemValue) return `Concatenation Value must be entered on item ${idx + 1}`;
            break;
          case concatenationTypes.mdmField:
            /* MDM Field - any other field in the current Table. */
            if (!value.itemValue) return `MDM Field must be selected on item ${idx + 1}`;
            /* Deep validation */
            if (currentField && value.itemValue === currentField.fieldGuid) return `MDM Field cannot be the same as the current field on item ${idx + 1}`;
            if (allFields) {
              const field = allFields.find((fld) => fld.fieldGuid === value.itemValue);
              if (!field) return `MDM Field does not exist on item ${idx + 1}`;
              if (currentField && field.entityGuid !== currentField.entityGuid) return `MDM Field must be in the same Table as the current field on item ${idx + 1}`;
            }
            break;
          case concatenationTypes.mdmFieldFromRelatedTable:
            if (!value.itemValue?.lookupFieldGuid) return `Lookup Field must be selected on item ${idx + 1}`;
            if (!value.itemValue?.fieldToDisplayGuid) return `Display Field must be selected on item ${idx + 1}`;
            /* Deep validation */
            if (allFields) {
              /* Lookup field */
              const lookupField = allFields.find((fld) => fld.fieldGuid === value.itemValue.lookupFieldGuid);
              if (!lookupField) return `Lookup Field does not exist on item ${idx + 1}`;
              if (!dataTypeIsLookup(getFieldDataType(lookupField))) return `Lookup Field must be type 'Lookup' on item ${idx + 1}`;
              /* Lookup Field's Display Field */
              const lookupDisplayField = allFields.find((fld) => fld.fieldGuid === getFieldDataTypeProperty(lookupField, 'FieldToDisplayGuid'));
              if (!lookupDisplayField) return `Lookup Field's Display Field does not exist on item ${idx + 1}`;
              /* Display Field */
              const displayField = allFields.find((fld) => fld.fieldGuid === value.itemValue.fieldToDisplayGuid);
              if (!displayField) return `Display Field does not exist on item ${idx + 1}`;
              if (displayField.entityGuid !== lookupDisplayField.entityGuid) return `Display Field must be in the same Table as the Lookup Field's Display Field on item ${idx + 1}`;
            }
            break;
          default:
            return `Unrecognised Concatenation type: ${value.itemType}`;
        }
      }
      break;

    /* ----------------------------------------------------------- */
    /* Calculation Type - AI Chat. */
    case calculationTypes.AIChat:

      if (!calculationData.provider) return 'AI Provider must be selected';
      if (!calculationData.providerModel) return 'AI Provider Model must be selected';
      if ((calculationData.question || '').trim().length < 1) return 'AI question is missing';
      if (!calculationData.responseType) return 'Response type must be selected';

      /* Deep validation. */
      calculationData.question.matchAll(/{{(.*?)}}/g)
        .forEach((fieldGuid) => {
          /* Check that the current Field is not referenced in the AI Question. */
          if (currentField && fieldGuid === currentField.fieldGuid) return 'AI field cannot be referenced in it\'s own AI Question';
          /* Get the field definition. */
          const field = (allFields || []).find((field) => { field.fieldGuid === fieldGuid; });
          /* Check that the field exists. */
          if (allFields && !field) return `AI Question includes Field ${fieldGuid} that does not exist`;
          /* Check that the Field belongs to the current Table. */
          if (currentField && field && field.entityGuid !== currentField.entityGuid) return `AI Question includes Field '${field.displayName}' that is not in the current Table`;
        });
      break;

    /* ----------------------------------------------------------- */
    /* Calculation Type - Row Formula. */
    case calculationTypes.RowFormula:
      if ((calculationData.formula || '').trim().length < 1) return 'Formula is missing';
      if (!/\breturn\b/ig.test(calculationData.formula)) return 'Formula must return a value';
      break;

    /* ----------------------------------------------------------- */
    /* Calculation Types - Aggregation & MDM Aggregation. */
    case calculationTypes.Aggregation:
    case calculationTypes.MdmAggregation:
      // Field.
      if ((calculationData.aggregationFieldGuid || '').trim().length < 1) return 'Aggregation Field is missing';
      /* Field - Deep validation */
      if (currentField && calculationData.aggregationFieldGuid == currentField.fieldGuid) return 'Aggregation Field cannot be the same as the current field';
      if (allFields) {
        const aggregationField = allFields?.find(fld => fld.fieldGuid == calculationData.aggregationFieldGuid);
        if (!aggregationField) return 'Aggregation field does not exist';
        if (currentField) {
          if (fieldCalculationType == calculationTypes.Aggregation && aggregationField.entityGuid != currentField.entityGuid)
            return 'Aggregation Field must be in the same Table as the current Field';
          if (fieldCalculationType == calculationTypes.MdmAggregation && aggregationField.entityGuid == currentField.entityGuid)
            return 'Aggregation Field must not be in the same Table as the current Field';
          if (calculationData.aggregateFunction && !fieldAllowsAggregateFunction(aggregationField, calculationData.aggregateFunction))
            return `Aggregation Field does not support the '${calculationData.aggregateFunction}' function`;
        }
      }
      // Function.
      if ((calculationData.aggregateFunction || '').trim().length < 1) return 'Aggregate Function is missing';
      // Filter.
      if ((calculationData.filterFormula || '').trim().length < 1) return 'Filter Formula is missing';
      if (!/\breturn\b/ig.test(calculationData.filterFormula)) return 'Filter Formula must return a value';
      break;

    /* ----------------------------------------------------------- */
    /* Calculation Type - Lookup. */
    case calculationTypes.lookup:

      /* Deep validation */
      let fieldToDisplayGuid = null;
      let fieldToDisplayGuidEntityGuid = null;
      if (currentField) {
        /* Field must be 'Lookup'. */
        if (!dataTypeIsLookup(getFieldDataType(currentField))) return 'Field is not a Lookup field';
        /* Field to Display. */
        fieldToDisplayGuid = getFieldDataTypeProperty(currentField, 'FieldToDisplayGuid');
        if (!fieldToDisplayGuid) return 'Field does not have a valid Related Table Column';
        /* Identify the related Table. */
        if (allFields) {
          const fieldToDisplay = allFields.find((fld) => fld.fieldGuid === fieldToDisplayGuid);
          if (!fieldToDisplay) return `Field's Related Table Column does not exist`;
          fieldToDisplayGuidEntityGuid = fieldToDisplay.entityGuid;
        }
      }

      /* Validate Lookup Pairs. */
      const lookupPairs = calculationData.sourceRelatedFieldPair;
      if (!lookupPairs?.length) return 'At least one Relation must be supplied.';
      for (let idx = 0; idx < lookupPairs.length; idx++) {
        const lookupPair = lookupPairs[idx];
        if (!lookupPair.sourceField) return `Source Field must be selected on item ${idx + 1}`;
        if (!lookupPair.relatedField) return `Related Field must be selected on item ${idx + 1}`;
        if (lookupPairs.find((pair, i) => { return i != idx && pair.sourceField === lookupPair.sourceField && pair.relatedField === lookupPair.relatedField; }))
          return `Duplicate Relation found for item ${idx + 1}`;

        /* Deep validation */
        if (allFields) {
          /* Source Field definition */
          const sourceField = allFields.find((fld) => fld.fieldGuid === lookupPair.sourceField);
          if (!sourceField) return `Source Field does not exist for item ${idx + 1}`;

          /* Source Field must be in current Table. */
          if (currentField && sourceField.entityGuid !== currentField.entityGuid) return `Source Field is not in the current Table for item ${idx + 1}`;

          /* Source Field must not be a lookup. */
          const sourceFieldType = getFieldDataType(sourceField);
          if (dataTypeIsLookup(sourceFieldType)) return `Source Field cannot be a Lookup field for item ${idx + 1}`;

          /*  Source field must not be RLS.*/
          if (sourceField.isRLSField) return `Source Field cannot be RLS for item ${idx + 1}`;

          /* Related Field definition */
          const relatedField = allFields.find((fld) => fld.fieldGuid === lookupPair.relatedField);
          if (!relatedField) return `Related Field does not exist for item ${idx + 1}`;

          /* Related Field must be in same Table as Field to Display. */
          if (fieldToDisplayGuidEntityGuid && relatedField.entityGuid != fieldToDisplayGuidEntityGuid) return `Related Field is not in the same Table as the Source Field's target Field for item ${idx + 1}`;

          /* Related Field must be same type as Source Field. */
          if (getFieldDataType(relatedField) !== sourceFieldType) return `Related Field must be the same type as the Source Field for item ${idx + 1}`;

          /*  Related field must not be RLS.*/
          if (relatedField.isRLSField) return `Related Field cannot be RLS for item ${idx + 1}`;
        }
      }
      break;

    /* ----------------------------------------------------------- */
    /* Calculation Type - Allocation. */
    case calculationTypes.allocation:
      break;

    /* ----------------------------------------------------------- */
    default:
      return `Unrecognised Calculation type: ${fieldCalculationType}`;
  }
};

/* ---------------------------------------------------------- */